import React from "react";
import BreadCrumbs from "@surgia/ui/components/breadCrumb";
import Button from "@surgia/ui/components/button";

interface SkeletonProps {
  className?: string;
}

const Skeleton: React.FC<SkeletonProps> = ({ className }) => (
  <div className={`animate-pulse rounded-md bg-gray-200 ${className || ""}`} />
);

const ProductDetailsSkeleton: React.FC = () => {
  return (
    <div className="flex flex-col items-start p-0 relative w-full min-h-screen bg-white">
      {/* Page Header */}
      <div className="flex flex-col items-start p-0 gap-4 w-full h-[121px] flex-none order-0 self-stretch flex-grow-0 z-0">
        {/* Breadcrumb */}
        <div className="w-full px-4 sm:px-6 pt-6">
          <BreadCrumbs
            items={[
              { label: "Tenants" },
              {
                label: "Tenant details",
              },
              {
                label: "Edit product",
              },
            ]}
          />
        </div>

        {/* Header Content */}
        <div className="w-full px-4 sm:px-6 pb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-1">
                Edit product
              </h1>
              <Skeleton className="h-4 w-48" />
            </div>
            <div className="flex items-center gap-4">
              <Button variant="outline" size="sm" disabled>
                Back
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex flex-col-reverse md:flex-row w-full flex-1 p-4 sm:p-6 gap-4 sm:gap-6">
        {/* Side Panel Skeleton */}
        <div className="w-full md:w-80 lg:w-96 flex-shrink-0">
          <div className="bg-white border border-gray-200 shadow-sm rounded-xl overflow-hidden">
            {/* Product Image Skeleton */}
            <Skeleton className="w-full h-48 sm:h-56" />

            {/* Product Names Skeleton */}
            <div className="px-4 sm:px-6 py-4">
              <div className="mb-6">
                <Skeleton className="h-5 w-3/4 mb-2" />
                <Skeleton className="h-4 w-2/3" />
              </div>

              <Skeleton className="h-5 w-16 mb-3" />

              {/* Variants List Skeleton */}
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="flex items-start gap-3 p-2">
                    <Skeleton className="w-4 h-4 sm:w-5 sm:h-5 mt-0.5 flex-shrink-0" />
                    <Skeleton className="h-4 w-full" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Area Skeleton */}
        <div className="flex-1">
          <div className="space-y-4">
            {/* Product Card Skeleton */}
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
              {/* Product Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <Skeleton className="h-6 w-3/4 mb-2" />
                  <Skeleton className="h-4 w-1/2" />
                </div>
                <Skeleton className="w-16 h-16 rounded-lg ml-4" />
              </div>

              {/* Product Details Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className="flex flex-col gap-2">
                    <Skeleton className="h-4 w-20" />
                    <Skeleton className="h-10 w-full rounded-lg" />
                  </div>
                ))}
              </div>

              {/* Variants Section */}
              <div className="border-t pt-4">
                <Skeleton className="h-5 w-24 mb-4" />
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between p-3 border rounded-lg"
                    >
                      <div className="flex items-center gap-3 flex-1">
                        <Skeleton className="w-5 h-5" />
                        <div className="flex-1">
                          <Skeleton className="h-4 w-3/4 mb-1" />
                          <Skeleton className="h-3 w-1/2" />
                        </div>
                      </div>
                      <Skeleton className="w-12 h-12 rounded-lg" />
                    </div>
                  ))}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                <Skeleton className="h-9 w-20" />
                <Skeleton className="h-9 w-24" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailsSkeleton;
